import _ from 'lodash';

/**
 * Angular Service that allows you to register handlers that receive
 * information about the element (or window) that was scrolled
 */
export class ScrollManagerService {
	/**
	 * No injections
	 */
	constructor() {
		'ngInject';
		/**
		 * The callbacks to fire when the target element is scrolled
		 * @type {Array}
		 */
		this.listeners = [];
	}

	/**
	 * Get info about the current scroll position
	 * @param element
	 * @returns {{ratioTop: number, ratioRight: number, scrollBottom: number, ratioLeft: number, ratioBottom: number, scrollLeft: number, scrollTop: number, scrollRight: number, ratio: number}}
	 */
	getPosition(element) {
		// NOTE: By default, if there are no scrollers available to an element in a display, its scrollLeft and scrollTop
		// values will be zero. Our initialization here represents that.
		let scrollLeft = 0,
			scrollTop = 0;
		let scrollRight, scrollBottom, ratioLeft, ratioTop, ratioRight, ratioBottom, ratio;

		// We need to find a single DOMElement object to use as the target for this look up, which we can identify from
		// several strategies, depending on what is supplied as the "element" argument. But, by default, we're going to
		// start with NULL, which means that no such object has been found.
		let target = null;

		// The strategy allows sending "window" or "document" as the target, rather than a specific DOM element within those
		// parent objects. If either one of these is specified, we choose the "document.documentElement" as the usable target.
		if (element === window || element === document) {
			target = document.documentElement;
		}
		// Another possibility is that "element" comes in as a NodeList rather than just a single Node. If that happens,
		// we attempt to find the first node of its contents THAT IS ALSO an element node. (Because other types of nodes
		// won't have scroll properties for us to actually read.)
		else if (element instanceof NodeList) {
			target = Array.from(element).reduce((target, node) => target || (node.nodeType === 1 ? node : null), null);
			// LOGIC: Reduce to the first node that qualifies as an ELEMENT_NODE, for which the code is "1".
			// NOTICE: If none of the node in NodeList qualifies, then target remains NULL, as initialized.
		}
		// And of course, the main case handled by this method is the single DOM element, which we check for here.
		else if (element instanceof Node && element.nodeType === 1) {
			target = element;
		}

		// So, if we were able to identify a valid target, we can now calculate the desired measurements and ratios.
		if (target) {
			const horizonalRange = target.scrollWidth - target.clientWidth; // NOTE: This will be a small number of pixels.
			const verticalRange = target.scrollHeight - target.clientHeight; // NOTE: This could be a large number of pixels.

			// scrollLeft and scrollTop are provided directly from the DOM element properties.
			scrollLeft = target.scrollLeft;
			scrollTop = target.scrollTop;

			// scrollRight and scrollBottom must be calculated from a combination DOM element properties.
			scrollRight = horizonalRange - scrollLeft;
			scrollBottom = verticalRange - scrollTop;

			// The ratios tell us how close the current scroll position is to the sides of its range. They are all relative
			// to the extent of their ranges.

			// NOTE: When scrolled all the way left, ratioLeft is 1. When scrolled all the way right, ratioLeft is 0.
			ratioLeft = (horizonalRange - scrollLeft) / horizonalRange;
			// NOTE: When scrolled all the way left, ratioRight is 0. When scrolled all the way right, ratioRight is 1.
			ratioRight = scrollLeft / horizonalRange;
			// NOTE: When scrolled all the way up, ratioTop is 1. When scrolled all the way down, ratioTop is 0.
			ratioTop = (verticalRange - scrollTop) / verticalRange;
			// NOTE: When scrolled all the way up, ratioBottom is 0. When scrolled all the way down, ratioBottom is 1.
			ratioBottom = scrollTop / verticalRange;

			// NOTE: For legacy reason, we are also including the "ratio" property, which is really just the same as the
			// ratioBottom calculation:
			ratio = scrollTop / (target.scrollHeight - target.clientHeight);
		}

		return {
			scrollLeft,
			scrollTop,
			scrollRight,
			scrollBottom,
			ratioLeft,
			ratioTop,
			ratioRight,
			ratioBottom,
			ratio,
		};
	}

	/**
	 * Get a function to handle scroll events
	 * @param {Element} element  The node (or window) to watch for scroll events
	 * @param {Function} handler  The callback function
	 * @returns {Function}
	 */
	handleScroll(element, handler) {
		if (!element) {
			return () => {};
		}
		return () => {
			handler(this.getPosition(element));
		};
	}

	/**
	 * Register a function to receive distance calculation on scroll events
	 * @param {Element} element  The node (or window) to observe
	 * @param {Function} handler  The callback function that will receive an object
	 * with properties scrollTop, scrollBottom, ratioTop, ratioBottom, ratio
	 * @param {Number} debounceMs  The number of milliseconds to debounce the scroll events
	 * @chainable
	 * @returns {ScrollManagerService}
	 */
	register(element, handler, debounceMs = 200) {
		let onScroll = _.throttle(this.handleScroll(element, handler), debounceMs, {
			leading: true,
			trailing: true,
		});
		angular.element(element).on('scroll', onScroll);
		this.listeners.push({ element, handler, onScroll });
		return this;
	}

	/**
	 * Unregister a previously registered callback
	 * @param {Element} element  The node (or window) that was observed
	 * @param {Function} handler  The callback function that was used
	 * @chainable
	 * @returns {ScrollManagerService}
	 */
	unregister(element, handler) {
		this.listeners = this.listeners.filter(registered => {
			if (registered.element === element && registered.handler === handler) {
				angular.element(registered.element).off('scroll', registered.onScroll);
				return false;
			}
			return true;
		});
		return this;
	}
}
